import dayjs from 'dayjs'

import { amount_to_tokens } from '../helpers/block_helpers'
import type { KYCTokenOptionsType } from '../kyc'
import { useGetSymbolToDecimal } from '../portfolio'
import type { ProductSymbol } from '../products'

import {
  type KYCLevel,
  useGetSDDPurchaseStats,
  useGetSDDSettings,
} from './token_auth_queries'

export function useGetSDDLimits({ symbol }: { symbol: ProductSymbol }) {
  const { data: settings } = useGetSDDSettings({ symbol })
  const { data: purchase_stats } = useGetSDDPurchaseStats({ symbol })
  const symbol_to_decimal = useGetSymbolToDecimal()

  const todays_last_time = purchase_stats?.todays_last_time
  const durations_last_time = purchase_stats?.durations_last_time

  const isSuccess =
    symbol_to_decimal.isSuccess && settings != null && purchase_stats != null

  if (!isSuccess) {
    return {
      isSuccess,
    } as const
  }

  const purchases_tokens_left_today = amount_to_tokens(
    settings.maxSingleDayDepositUSDCAmount -
      purchase_stats.deposits_today_USDC_amount,
    symbol_to_decimal['USDC'],
  )
  const purchases_tokens_left_duration = amount_to_tokens(
    settings.maxDurationDepositUSDCAmount -
      purchase_stats.deposits_over_duration_USDC_amount,
    symbol_to_decimal['USDC'],
  )
  const max_single_purchase = amount_to_tokens(
    settings.maxSingleDepositUSDCAmount,
    symbol_to_decimal['USDC'],
  )

  const purchases_left_duration =
    settings.maxDurationDeposits - Number(purchase_stats.deposits_over_duration)

  return {
    isSuccess,
    purchases_left_today: Math.min(
      settings.maxSingleDayDeposits - purchase_stats.deposits_today,
      purchases_left_duration,
    ),
    purchases_left_duration,
    purchases_tokens_left_today,
    purchases_tokens_left_duration,
    max_purchase_usdc_tokens: Math.min(
      purchases_tokens_left_today,
      purchases_tokens_left_duration,
      max_single_purchase,
    ),
    last_todays_purchase_time:
      todays_last_time == null ? null : dayjs.unix(Number(todays_last_time)),
    last_duration_purchase_time:
      durations_last_time == null
        ? null
        : dayjs.unix(Number(durations_last_time)),
  }
}

export function useNextPurchaseLevel({
  symbol,
  deposit_usdc_tokens,
}: {
  symbol: ProductSymbol
  deposit_usdc_tokens: number
}): {
  level: KYCTokenOptionsType['level']
  isSuccess: boolean
  allowed_kyc_levels: KYCLevel[]
} {
  const { isSuccess, max_purchase_usdc_tokens, purchases_left_today } =
    useGetSDDLimits({ symbol })

  if (isSuccess == false) {
    return { isSuccess: false, level: 'full', allowed_kyc_levels: ['full'] }
  }

  if (
    deposit_usdc_tokens > max_purchase_usdc_tokens ||
    purchases_left_today <= 0
  ) {
    return { isSuccess: true, level: 'full', allowed_kyc_levels: ['full'] }
  }

  return {
    isSuccess: true,
    level: 'sdd',
    allowed_kyc_levels: ['sdd', 'full'],
  }
}
